import React, { useState, useEffect } from "react";
import {
  ArrowLeft,
  Wallet,
  TrendingUp,
  CheckCircle,
  AlertCircle,
  Loader,
  Shield,
  CreditCard,
} from "lucide-react";
import { walletService } from "../services/walletService";
import { useAuth } from "../contexts/AuthContext";
import { formatMoney, parseMoney } from "../utils/money";

const Deposit = ({ preSelectedWallet = null, onBack, walletBalances }) => {
  const { updateUserBalance } = useAuth();
  const [currentStep, setCurrentStep] = useState(
    preSelectedWallet ? "form" : "selection",
  );
  const [selectedWallet, setSelectedWallet] = useState(preSelectedWallet);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Form states
  const [formData, setFormData] = useState({
    amount: "",
    payment_phone: "",
    network: "mtn",
    reference: "",
  });

  // Deposit flow states
  const [depositResult, setDepositResult] = useState(null);

  // Polling states
  const [polling, setPolling] = useState(false);
  const [pollCount, setPollCount] = useState(0);

  const networks = [
    {
      value: "mtn",
      label: "MTN ",
      icon: "/mtn-momo.png",
      color: "#FFCC00",
      bgColor: "bg-yellow-50",
    },
    {
      value: "telecel",
      label: "Telecel",
      icon: "/telecel-cash.png",
      color: "#0066CC",
      bgColor: "bg-blue-50",
    },
    {
      value: "airteltigo",
      label: "AT",
      icon: "/at-money.png",
      color: "#E60012",
      bgColor: "bg-red-50",
    },
  ];

  const walletTypes = [
    {
      type: "affordable_data",
      title: "Affordable Data",
      subtitle: "Discounted packages",
      balance: walletBalances?.affordable_data_balance || 0,
      commissionRate: 0,
      gradient: "from-slate-700 to-slate-900",
      icon: Wallet,
      accent: "#374151",
    },
    {
      type: "airtime_data",
      title: "Airtime & Data",
      subtitle: "Business reseller wallet",
      balance: walletBalances?.airtime_data_balance || 0, // ✅ Fresh balance
      commissionRate: walletBalances?.commission_rate || 2.5, // ✅ Fresh commission rate
      gradient: "from-yellow-400 to-yellow-900",
      icon: TrendingUp,
      accent: "darkgoldenrod",
    },
  ];

  // Reset form when wallet changes
  useEffect(() => {
    if (selectedWallet) {
      setFormData((prev) => ({
        ...prev,
        reference: `${selectedWallet.replace("_", " ")} wallet top-up`,
      }));
    }
  }, [selectedWallet]);

  // Auto-polling effect
  // Auto-polling effect
  useEffect(() => {
    let pollInterval;

    // Move checkTransactionStatus inside useEffect
    const checkTransactionStatus = async () => {
      if (!depositResult?.deposit_ref) return;

      try {
        const response = await walletService.checkStatus(
          depositResult.deposit_ref,
        );

        if (response.status === "success" && response.data) {
          const status = response.data.status;

          if (status === "completed") {
            setPolling(false);
            setCurrentStep("success");
            setDepositResult(response.data);

            // 🔥 Refresh wallet balance after successful deposit
            try {
              const balanceResponse = await walletService.getBalance();
              if (balanceResponse.status === "success") {
                updateUserBalance(balanceResponse.data);
                console.log("Balance updated:", balanceResponse.data);
              }
            } catch (balanceErr) {
              console.log("Balance refresh error:", balanceErr);
            }
          } else if (status === "failed") {
            setPolling(false);
            setError("Payment failed. Please try again.");
            setCurrentStep("form");
          }
        }
      } catch (err) {
        console.log("Status check error:", err);
      }
    };

    if (currentStep === "waiting" && depositResult?.deposit_ref) {
      setPolling(true);
      setPollCount(0);

      pollInterval = setInterval(async () => {
        setPollCount((count) => {
          const newCount = count + 1;

          if (newCount >= 60) {
            setPolling(false);
            clearInterval(pollInterval);
            return newCount;
          }

          checkTransactionStatus();
          return newCount;
        });
      }, 5000);
    }

    return () => {
      if (pollInterval) {
        clearInterval(pollInterval);
      }
    };
  }, [currentStep, depositResult?.deposit_ref, updateUserBalance]); // Now no missing dependencies!

  // Manual check function
  const handleManualCheck = async () => {
    setLoading(true);

    if (!depositResult?.deposit_ref) {
      setLoading(false);
      return;
    }

    try {
      const response = await walletService.checkStatus(
        depositResult.deposit_ref,
      );

      if (response.status === "success" && response.data) {
        const status = response.data.status;

        if (status === "completed") {
          setPolling(false);
          setCurrentStep("success");
          setDepositResult(response.data);

          // 🔥 Refresh balance here too
          try {
            const balanceResponse = await walletService.getBalance();
            if (balanceResponse.status === "success") {
              updateUserBalance(balanceResponse.data);
            }
          } catch (balanceErr) {
            console.log("Balance refresh error:", balanceErr);
          }
        } else if (status === "failed") {
          setPolling(false);
          setError("Payment failed. Please try again.");
          setCurrentStep("form");
        }
      }
    } catch (err) {
      console.log("Status check error:", err);
    }

    setLoading(false);
  };

  // Handle wallet selection
  const handleWalletSelect = (walletType) => {
    setSelectedWallet(walletType);
    setCurrentStep("form");
    setError("");
  };

  // Handle form input changes
  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    setError("");
  };

  // Validate form
  const validateForm = () => {
    if (!formData.amount || parseMoney(formData.amount) < 0.5) {
      setError("Minimum deposit amount is GHS 0.50");
      return false;
    }
    if (!formData.payment_phone || formData.payment_phone.length < 10) {
      setError("Please enter a valid phone number");
      return false;
    }
    if (!formData.network) {
      setError("Please select a network");
      return false;
    }
    return true;
  };

  // Handle deposit initiation
  const handleInitiateDeposit = async () => {
    if (!validateForm()) return;

    setLoading(true);
    setError("");

    try {
      const depositData = {
        ...formData,
        wallet_type: selectedWallet,
        amount: parseMoney(formData.amount),
      };

      const response = await walletService.initiateDeposit(depositData);

      // Fix: Check response.status instead of response.success
      if (response.status === "success") {
        setDepositResult(response.data);

        // Check the step to determine which screen to show
        if (response.data.step === "payment_sent") {
          setCurrentStep("waiting"); // New state for waiting for user approval
        } else if (response.data.step === "completed") {
          setCurrentStep("success");
        } else {
          // Handle any other steps that might exist
          setCurrentStep("waiting");
        }
      } else {
        setError(response.message || "Failed to initiate deposit");
      }
    } catch (err) {
      setError(err.response?.data?.message || "Failed to initiate deposit");
    } finally {
      setLoading(false);
    }
  };

  // Handle back navigation
  const handleBack = () => {
    if (currentStep === "form" && !preSelectedWallet) {
      // Only go to selection if we started without a preselected wallet
      setCurrentStep("selection");
      setSelectedWallet(null);
    } else if (onBack) {
      // If we have a preSelectedWallet OR we're on selection step, use onBack
      onBack();
    }
    setError("");
  };

  // Handle restart
  const handleRestart = () => {
    setCurrentStep(preSelectedWallet ? "form" : "selection");
    setSelectedWallet(preSelectedWallet);
    setFormData({
      amount: "",
      payment_phone: "",
      network: "mtn",
      reference: "",
    });
    setDepositResult(null);
    setError("");
    setPolling(false);
    setPollCount(0);
  };

  // Get current wallet info
  const getCurrentWallet = () => {
    return walletTypes.find((w) => w.type === selectedWallet);
  };

  // Render wallet selection screen
  const renderWalletSelection = () => (
    <div className="space-y-8">
      <div className="text-center">
        <h1 className="text-2xl font-bold text-gray-600 mb-0">Choose Wallet</h1>
        <p className="text-gray-600 text-lg font-light">
          Select which wallet to top up
        </p>
      </div>

      <div className="space-y-4">
        {walletTypes.map((wallet) => (
          <div
            key={wallet.type}
            onClick={() => handleWalletSelect(wallet.type)}
            className={`relative overflow-hidden bg-gradient-to-r ${wallet.gradient} rounded-2xl max-w-[400px] mx-auto p-6 shadow-xl hover:shadow-2xl transition-all duration-300 cursor-pointer group border border-gray-200`}
          >
            <div className="flex  justify-between">
              <div className="flex  space-x-3">
                <div
                  className="w-14 h-14 rounded-2xl flex items-center justify-center shadow-lg"
                  style={{ backgroundColor: wallet.accent }}
                >
                  <wallet.icon className="w-7 h-7 text-white" />
                </div>
                <div>
                  <h3 className="text-white font-bold text-lg sm:text-xl">
                    {wallet.title}
                  </h3>
                  <p className="text-white/80 text-sm">{wallet.subtitle}</p>
                  {wallet.commissionRate > 0 && (
                    <div className="mt-2 inline-flex items-center px-2 py-1 rounded-xl bg-slate-700 backdrop-blur-sm">
                      <span className="text-white text-xs font-medium">
                        +{wallet.commissionRate}% bonus
                      </span>
                    </div>
                  )}
                </div>
              </div>
              <div className="text-right">
                <p className="text-white/70 text-sm font-medium">Balance</p>
                <p className="text-white font-bold text-xl sm:text-2xl">
                  ₵{formatMoney(wallet.balance)}
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  // Render deposit form
  const renderDepositForm = () => {
    const currentWallet = getCurrentWallet();

    return (
      <div className="space-y-1">
        <div className="space-y-5">
          {/* Header */}
          <div className="flex items-center space-x-4">
            <button
              onClick={handleBack}
              className="p-3 rounded-xl bg-gray-100 hover:bg-gray-200 transition-colors"
            >
              <ArrowLeft className="w-7 h-7 text-gray-700" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-700 ">Add Money</h1>
              <p className="text-gray-600">
                Top up your {currentWallet?.title.toLowerCase()} wallet
              </p>
            </div>
          </div>

          {/* Current wallet summary */}
          <div
            className={`relative overflow-hidden bg-gradient-to-r ${currentWallet?.gradient} rounded-2xl p-6 shadow-lg`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center shadow-lg"
                  style={{ backgroundColor: currentWallet?.accent }}
                >
                  <currentWallet.icon className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-white font-bold text-lg">
                    {currentWallet?.title}
                  </h3>
                  <p className="text-white/80 text-sm">Current balance</p>
                </div>
              </div>
              <p className="text-white font-bold text-2xl">
                ₵{formatMoney(currentWallet?.balance)}
              </p>
            </div>
          </div>
        </div>

        {/* Form */}
        <div className=" rounded-2xl  p-8 space-y-8">
          {/* Amount Section */}
          <div>
            <label className="block text-base font-light text-gray-900  mb-2">
              Amount to deposit
            </label>
            <div className="relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 font-medium">
                ₵
              </span>
              <input
                type="number"
                step="0.01"
                min="0.5"
                value={formData.amount}
                onChange={(e) => handleInputChange("amount", e.target.value)}
                placeholder="0.00"
                className="w-full pl-8 pr-4 py-4 text-2xl font-bold text-gray-900 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all outline-none"
              />
            </div>
            <p className="text-xs text-gray-500 mt-2">
              Minimum deposit: <span className="text-yellow-600">₵0.50</span>
            </p>

            {/* Quick amount buttons */}
            <div className="mt-4 flex space-x-3">
              {[5, 10, 20, 50].map((amount) => (
                <button
                  key={amount}
                  onClick={() => handleInputChange("amount", amount.toString())}
                  className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium text-gray-700 transition-colors"
                >
                  ₵{amount}
                </button>
              ))}
            </div>
          </div>

          {/* Network Selection */}
          <div>
            <label className=" text-base font-light text-gray-900 mb-4">
              Select network
            </label>
            <div className="grid grid-cols-3 gap-3 pt-1">
              {networks.map((network) => (
                <button
                  key={network.value}
                  onClick={() => handleInputChange("network", network.value)}
                  className={`flex items-center p-2 rounded-xl border-2 transition-all ${
                    formData.network === network.value
                      ? "border-yellow-500 bg-slate-50 shadow-xl"
                      : "border-gray-200 hover:border-gray-300 bg-white"
                  }`}
                >
                  <div className="flex-1 text-left">
                    <p className="font-semibold text-sm text-gray-900 text-center">
                      {network.label}
                    </p>
                  </div>
                  {formData.network === network.value && (
                    <CheckCircle className="w-4 h-4 text-emerald-500" />
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Phone Number */}
          <div>
            <label className="block text-base font-light text-gray-900  mb-2">
              Mobile money number
            </label>
            <input
              type="tel"
              value={formData.payment_phone}
              onChange={(e) =>
                handleInputChange("payment_phone", e.target.value)
              }
              placeholder="024 123 4567"
              className="w-full px-4 py-4 text-lg bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all outline-none"
            />
          </div>

          {/* Commission info */}
          {currentWallet?.commissionRate > 0 && (
            <div className="bg-green-50 border border-green-200 rounded-xl p-4">
              <div className="flex items-start space-x-3">
                <Shield className="w-5 h-5 text-green-600 mt-0.5" />
                <div>
                  <p className="text-green-800 font-medium">Bonus included</p>
                  <p className="text-green-700 text-sm">
                    {currentWallet.commissionRate}% commission will be added to
                    your deposit
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4">
              <div className="flex items-start space-x-3">
                <AlertCircle className="w-5 h-5 text-red-600 mt-0.5" />
                <p className="text-red-700 text-sm">{error}</p>
              </div>
            </div>
          )}

          {/* Submit button */}
          <button
            onClick={handleInitiateDeposit}
            disabled={loading}
            className="w-full bg-gray-900 hover:bg-gray-800 text-white font-semibold py-4 px-6 rounded-xl transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-3 shadow-lg"
          >
            {loading ? (
              <>
                <Loader className="w-5 h-5 animate-spin" />
                <span>Processing...</span>
              </>
            ) : (
              <>
                <CreditCard className="w-5 h-5" />
                <span>Top Up</span>
              </>
            )}
          </button>
        </div>
      </div>
    );
  };

  // Render waiting for approval screen - Compact Mobile Design
  const renderWaiting = () => (
    <div className="h-screen flex items-center justify-center px-4">
      <div className="w-full max-w-sm space-y-6">
        {/* Status Icon & Animation */}
        <div className="text-center">
          <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Loader className="w-8 h-8 text-yellow-600 animate-spin" />
          </div>
          <h1 className="text-xl font-bold text-gray-900 mb-2">
            Waiting for Payment
          </h1>
          <p className="text-gray-600 text-sm">
            Check your phone and enter your MOMO PIN
          </p>
        </div>

        {/* Payment Summary Card - Compact */}
        {depositResult && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-gray-600 text-sm">Amount</span>
                <span className="font-semibold">
                  ₵{formatMoney(depositResult.amount)}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600 text-sm">Phone</span>
                <span className="font-medium text-sm">
                  {depositResult.payment_phone}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600 text-sm">Network</span>
                <span className="font-medium text-sm capitalize">
                  {depositResult.network}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Status Message */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
          <div className="flex items-center space-x-3">
            <Shield className="w-5 h-5 text-blue-600 flex-shrink-0" />
            <div>
              <p className="text-blue-800 font-medium text-sm">Payment Sent</p>
              <p className="text-blue-700 text-xs">
                {polling
                  ? `Auto-checking status... (${Math.floor(
                      (pollCount * 5) / 60,
                    )}:${String(Math.floor((pollCount * 5) % 60)).padStart(
                      2,
                      "0",
                    )})`
                  : "Approve on your phone to continue"}
              </p>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="space-y-3">
          <button
            onClick={handleManualCheck}
            disabled={loading}
            className="w-full bg-yellow-400 hover:bg-yellow-500 text-black font-semibold py-3 px-4 rounded-xl transition-all duration-200 disabled:opacity-50 flex items-center justify-center space-x-2"
          >
            {loading ? (
              <>
                <Loader className="w-4 h-4 animate-spin" />
                <span>Checking...</span>
              </>
            ) : (
              <>
                <CheckCircle className="w-4 h-4" />
                <span>I have paid</span>
              </>
            )}
          </button>

          <button
            onClick={handleRestart}
            className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-3 px-4 rounded-xl transition-all duration-200"
          >
            Cancel & Start Over
          </button>
        </div>

        {/* Small Reference Info */}
        {depositResult && (
          <div className="text-center">
            <p className="text-gray-400 text-xs">
              Ref: {depositResult.deposit_ref}
            </p>
          </div>
        )}
      </div>
    </div>
  );

  // Render success screen
  const renderSuccess = () => (
    <div className="space-y-8 text-center max-w-md mx-auto">
      <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto">
        <CheckCircle className="w-10 h-10 text-green-600" />
      </div>

      <div>
        <h1 className="text-3xl font-bold text-gray-900 mb-3">
          Payment Completed
        </h1>
        <p className="text-gray-600 text-base sm:text-lg">
          Your wallet has been topped up successfully
        </p>
      </div>

      {depositResult && (
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 text-left">
          <h3 className="font-bold text-gray-900 mb-4 text-center">
            Transaction Summary
          </h3>
          <div className="space-y-3">
            <div className="flex justify-between items-center py-2 border-b border-gray-100">
              <span className="text-gray-600">Amount</span>
              <span className="font-semibold">
                ₵{formatMoney(depositResult.amount)}
              </span>
            </div>
            <div className="flex justify-between items-center py-2 border-b border-gray-100">
              <span className="text-gray-600">Commission</span>
              <span className="font-semibold text-green-600">
                +₵{formatMoney(depositResult.commission_amount || 0)}
              </span>
            </div>
            <div className="flex justify-between items-center py-2 border-b border-gray-100">
              <span className="text-gray-600 font-semibold">Total Credit</span>
              <span className="font-bold text-lg">
                ₵
                {formatMoney(
                  depositResult.total_credited ?? depositResult.total_credit,
                )}
              </span>
            </div>
            <div className="flex justify-between items-center py-2">
              <span className="text-gray-600">Reference</span>
              <span className="font-mono text-sm">
                {depositResult.deposit_ref}
              </span>
            </div>
          </div>
        </div>
      )}

      <button
        onClick={handleRestart}
        className="w-full bg-gray-800 hover:bg-gray-800 text-yellow-400 font-semibold py-4 px-6 rounded-3xl transition-all duration-200 shadow-lg"
      >
        Make Another Deposit
      </button>
    </div>
  );

  return (
    <div className="max-w-2xl mx-auto">
      {currentStep === "selection" && renderWalletSelection()}
      {currentStep === "form" && renderDepositForm()}
      {currentStep === "waiting" && renderWaiting()}
      {currentStep === "success" && renderSuccess()}
    </div>
  );
};

export default Deposit;
